import { Component, DestroyRef, effect, inject, OnInit, untracked } from '@angular/core';
import { Subject, switchMap, map, catchError, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DashboardFilters, DashboardService, MessagingFiltersPayload } from '../../../services/dashboard.service';
import { MessagingService } from '../../../services/messaging.service';
import { MessagingResponse } from '../types';
import { MessagingCategoryTableComponent } from '../messaging-category-table/messaging-category-table.component';
import { BrandHeaderComponent } from '../brand-header/brand-header.component';
import { SpinnerIconComponent } from '../../../shared/components/icons/spinner-icon/spinner-icon.component';
import { InfiniteScrollDirective } from '../../../shared/directives/infinite-scroll.directive';
import { CommonModule } from '@angular/common';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';

@Component({
  selector: 'app-marketing-report',
  imports: [MessagingCategoryTableComponent, BrandHeaderComponent, SpinnerIconComponent, InfiniteScrollDirective, CommonModule, AssetVersionPipe],
  templateUrl: './marketing-report.component.html',
  styleUrl: './marketing-report.component.scss'
})
export class MarketingReportComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private messagingService = inject(MessagingService);
  private destroyRef = inject(DestroyRef);

  public readonly MAX_BRAND_COLUMNS = 6;
  public readonly LIMIT = 10;
  public messagingData$ = new Subject<MessagingFiltersPayload>();
  public messagingData: MessagingResponse | undefined;
  public isLoading: boolean = false;
  private currentOffset: number = 0;

  constructor() {
    effect(() => {
      const dashboardFilters = this.dashboardService.brandsFilters();
      untracked(() => {
        if (dashboardFilters.myBrand?.key) {
          this.messagingData = undefined;
          this.currentOffset = 0;
          this.messagingData$.next(this.getMessagingPayload(dashboardFilters, 0));
        }
      });
    });
  }

  ngOnInit(): void {
    this.messagingData$.pipe(
      switchMap(payload => {
        const isInitialLoad = payload.offset === 0;
        this.isLoading = true;
        return this.messagingService.getMessagingData(payload).pipe(
          map(result => ({ result, isInitialLoad })),
          catchError(error => {
            console.error('Error fetching messaging data', error);
            this.isLoading = false;
            return of(null);
          })
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      if (!response) return;
      const { result, isInitialLoad } = response;
      if (isInitialLoad) {
        this.messagingData = result;
      } else {
        if (this.messagingData && result.categories.length > 0) {
          this.messagingData.categories = [...this.messagingData.categories, ...result.categories];
        }
      }
      this.isLoading = false;
    });
  }

  onLoadMore(): void {
    const dashboardFilters = this.dashboardService.brandsFilters();
    if (dashboardFilters.myBrand?.key && !this.isLoading) {
      this.currentOffset += this.LIMIT;
      this.messagingData$.next(this.getMessagingPayload(dashboardFilters, this.currentOffset));
    }
  }

  private getMessagingPayload(dashboardFilters: DashboardFilters, offset: number): MessagingFiltersPayload {
    const payload = this.dashboardService.getDashboardFiltersPayload(dashboardFilters);
    return {
      ...payload,
      brandKey: dashboardFilters.myBrand.key,
      competitorsKeys: dashboardFilters.myCompetitors.map(c => c.key),
      offset,
      limit: this.LIMIT
    };
  }
}

